import { greyMui, rydeColors } from "@ryde/theme";

import {
  AXIS_TICK_FONT_SIZE,
  AXIS_TITLE_FONT,
} from "./dashboard-chart.constants";
import {
  IDashboardAxis,
  IDashboardAxisOptions,
  IDashboardAxisTitle,
} from "./dashboard-chart.models";

/**
 * Axis-title style for the compact dashboard cards: the shared
 * `reportChartAxisTitle` color token with a smaller font than the reports drawer
 * (these cards are much smaller). Hidden when there is no label.
 */
export const dashboardAxisTitle = (label: string): IDashboardAxisTitle => ({
  display: Boolean(label),
  text: label,
  color: rydeColors.reportChartAxisTitle,
  font: AXIS_TITLE_FONT,
});

/**
 * Category (dimension) axis: no grid, dark ticks, optional title. On the
 * horizontal bar the category axis is Y — in RTL it moves to the right side.
 */
export const dashboardCategoryAxis = (
  title: string,
  {
    isRtl = false,
    isHorizontal = false,
    tickFontSize,
  }: IDashboardAxisOptions = {},
): IDashboardAxis => ({
  grid: { display: false },
  ticks: {
    color: greyMui._700,
    font: { size: tickFontSize ?? AXIS_TICK_FONT_SIZE },
  },
  title: dashboardAxisTitle(title),
  ...(isHorizontal ? { position: isRtl ? "right" : "left" } : {}),
});

/**
 * Value (metric) axis: zero-based, light grid, muted ticks, optional title.
 * In RTL the vertical/line value axis (Y) moves to the right; the horizontal
 * value axis (X) is reversed so bars grow right-to-left.
 */
export const dashboardValueAxis = (
  title: string,
  { isRtl = false, isHorizontal = false }: IDashboardAxisOptions = {},
): IDashboardAxis => ({
  beginAtZero: true,
  grid: { color: greyMui._200 },
  ticks: { color: greyMui._500, font: { size: AXIS_TICK_FONT_SIZE } },
  title: dashboardAxisTitle(title),
  ...(isHorizontal
    ? { reverse: isRtl }
    : { position: isRtl ? "right" : "left" }),
});

/** Default chart value formatter — a localized number (used when a card supplies none). */
export const defaultChartValueFormatter = (value: number): string =>
  Number(value).toLocaleString();

/** A missing / "None" dimension bucket (rendered in red on the vertical bar chart). */
export const isNoneDimensionLabel = (label: string): boolean =>
  !label || label.trim().toLowerCase() === "none";
